<template>
  <mu-card id="medication-input">
    <mu-card-text class="card-style" v-bind:class="{cardHasData: hasData}">
      <div v-if="loading">
        <mu-flexbox class="card-loading-padding" align="center" justify="center">
          <mu-flexbox-item align="center">
            <mu-circular-progress :size="56" color="#FFFFFF" :strokeWidth="5"/>
          </mu-flexbox-item>
        </mu-flexbox>
      </div>
      <div v-if="!loading" class="animated fadeIn">
        <mu-flexbox v-if="data.no_setting || data.error" class="card-error-padding" justify="center">
          <mu-flexbox-item align="center">
            <div v-if="hasMedications">
              <div class="card-notice-title" v-text="data.message"></div>
              {{ $t('message.cardTapPrefix') }}
              <mu-icon v-if="data.no_setting" class="card-icon-error" :size="16" slot="left" value="settings"/>
              <mu-icon v-if="data.error" class="card-icon-error" :size="16" slot="left" value="add"/>
              <span v-if="data.error || data.no_setting">{{ $t('message.cardTapSuffix') }}</span>
            </div>
            <div v-if="!hasMedications">
              <div class="card-notice-title">{{$t('message.cardEmptyMedication')}}</div>
              {{ $t('message.cardTapPrefix') }}
              <mu-icon class="card-icon-error" :size="16" slot="left" value="settings_cog"/>
              <span v-if="data.error || data.no_setting">{{ $t('message.cardTapSuffix') }}</span>
            </div>
          </mu-flexbox-item>
        </mu-flexbox>
        <div v-else @click="goToDetail" style="cursor: pointer">
          <span v-show="false">{{hasData = true}}</span>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                {{ $t('message.cardMedicationLastDose') }}
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                <div class="card-large-stat">
                  {{data.dosage}} {{localizeRegime(data.unit_label)}}
                </div>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
          <mu-content-block>
            <mu-flexbox>
              <mu-flexbox-item class="card-lite-title">
                {{ $t('message.cardMedicationLastMedication') }}
              </mu-flexbox-item>
              <mu-flexbox-item align="right">
                {{data.treatment_name}}
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-content-block>
        </div>
      </div>
    </mu-card-text>
    <mu-card-actions>
      <mu-flexbox justify="flex_start" style="height: 48px">
        <svg class="card-left-icon" width="16px" height="16px" viewBox="21 193 15 15" version="1.1"
             xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <defs>
            <path
              d="M1.00081633,9.15102041 C-0.331428571,10.4832653 -0.331428571,12.6546939 1.00081633,13.9869388 C2.33306122,15.3191837 4.5044898,15.3191837 5.83673469,13.9869388 L9.9477551,9.87591837 L5.11183673,5.03673469 L1.00081633,9.15102041 L1.00081633,9.15102041 Z"
              id="path-1"></path>
            <mask id="mask-2" maskContentUnits="userSpaceOnUse" maskUnits="objectBoundingBox" x="0" y="0"
                  width="9.94612245" height="9.94938776" fill="white">
              <use xlink:href="#path-1"></use>
            </mask>
          </defs>
          <g id="Group-5" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"
             transform="translate(21.000000, 193.000000)">
            <path
              d="M9.15020408,1.00163265 L4.56897959,5.56979592 L9.40489796,10.4057143 L13.9828571,5.83428571 C15.3183673,4.50204082 15.3183673,2.33387755 13.9828571,1.00163265 C12.6506122,-0.333877551 10.482449,-0.333877551 9.15020408,1.00163265 Z"
              id="Shape" fill="#AD3463"></path>
            <use id="Shape" stroke="#AD3463" mask="url(#mask-2)" stroke-width="2" xlink:href="#path-1"></use>
          </g>
        </svg>
        <mu-flexbox-item class="left16" v-text="loading ? $t('message.loadingText') : title"></mu-flexbox-item>
        <mu-flexbox-item align="right">
          <mu-icon-button
            v-if="!loading"
            class="card-bar-icon"
            icon="settings_cog"
            @click.native="goToSettings()"
            :disabled="loading"
          >
          </mu-icon-button>
          <mu-icon-button
            v-tooltip.top="$t('message.addEntryText')"
            v-if="hasMedications && !isOpenInput"
            class="card-bar-icon"
            :icon="'add'"
            @click.native="openInput"
            :disabled="loading"
          ></mu-icon-button>
          <mu-icon-button
            v-if="hasMedications && isOpenInput"
            class="card-bar-icon"
            :icon="'remove'"
            @click.native="openInput"
            :disabled="loading"
          ></mu-icon-button>
        </mu-flexbox-item>
      </mu-flexbox>
    </mu-card-actions>
    <transition :css="false" @enter="onToggleEnter" @leave="onToggleLeave">
      <form v-if="isOpenInput" v-on:submit.prevent="saveReading" novalidate v-bind:style="{height: computedAuto}"
            style="overflow: hidden">
        <mu-card-text v-if="hasMedications">


          <div style="margin-bottom: 16px" v-if="!alreadyCopied">
            <mu-raised-button :disabled="!hasMedsToCopy"
                              :label="$t('message.cardMedicationRepeatYesterday') + ' (' + yesterdayDosageCount + ')'" fullWidth
                              @click.native="copyYesterdaysMedication"></mu-raised-button>
          </div>


          <mu-select-field
            v-validate="'required'"
            data-vv-name="medication"
            data-vv-value-path="inputValue"
            :errorText="errors.first('medication')"
            data-vv-validate-on="none"
            :data-vv-as="$t('cardMedication')"
            :scroller="scroller"
            v-model="model.tracking_id"
            :label="$t('message.cardMedicationSelect')"
            :hintText="$t('message.selectText')"
            fullWidth
            autoWidth
          >
            <mu-menu-item
              v-for="item, index in activeMedications"
              :title="item.treatment_name"
              :value="item.tracking_id"
              :key="'medication-' + index"
            ></mu-menu-item>
          </mu-select-field>

          <mu-text-field
            class="bottom"
            min="1"
            max="2500"
            v-validate="'required|min_value:1|max_value:2500'"
            data-vv-name="dosage"
            data-vv-value-path="inputValue"
            :data-vv-as="$t('message.cardMedicationDose')"
            :errorText="errors.first('dosage')"
            data-vv-validate-on="none"
            :label="$t('message.cardMedicationDose')"
            type="number"
            v-model.number="model.dosage"
            :hintText="$t('message.cardMedicationDosage')"
            :helpText="localizeRegime(activeLabel)"
            fullWidth
          >
          </mu-text-field>

          <div class="bottom"></div>

          <mu-checkbox v-model="model.customDate" :label="$t('message.labelCustomDate')"></mu-checkbox>
          <date-picker v-if="model.customDate" type="datetime" v-model="model.date"></date-picker>

        </mu-card-text>
        <mu-card-actions align="right">
          <button type="submit" style="display: none"></button>
          <mu-flat-button
            :label="$t('message.discardText')"
            @click.native="closeInput"
            class="demo-flat-button"
          />
          <mu-flat-button
            @click.native="saveReading"
            :disabled="isSaving"
            :label="isSaving ? $t('message.savingText'): $t('message.saveText')"
            class="demo-flat-button"
          />
        </mu-card-actions>
      </form>
    </transition>
  </mu-card>
</template>
<script>

  import { TweenMax, TweenLite, Power3, ScrollToPlugin } from 'gsap/all'
  import 'gsap/ScrollToPlugin'
  const plugins = [ ScrollToPlugin];

  import MedicationApi from '../../../../api/Tracking/Medication/index'
  import DatePicker from "../../../DateTimePicker/DateTimePicker.vue";
  import CardAnimationMixin from '../../../../mixins/CardAnimationMixin'
  import MuFlatButton from "muse-ui/src/flatButton/flatButton";
  import MedicationRegimeMixin from '../../../../mixins/Localization/MedicationRegimes';

  export default {
    mixins: [CardAnimationMixin, MedicationRegimeMixin],
    components: {
      MuFlatButton,
      DatePicker,
      MedicationRegimeMixin
    },
    name: 'medication-input',
    data() {
      return {
        alreadyCopied: false,
        yesterdayDosageCount: 0,
        hasMedsToCopy: false,
        scroller: document.getElementsByClassName('main-content')[0],
        title: this.$t('message.cardMedication'),
        isOpen: false,
        isOpenInput: false,
        isSaving: false,
        isBackgroundShown: true,
        hasMedications: true,
        hasData: false,
        noData: false,
        loading: true,
        data: {},
        medications: [],
        model: {
          tracking_id: null,
          dosage: null
        }
      }
    },
    mounted() {
      let self = this;

      if (window.sessionStorage.getItem('copiedMeds') !== null) {
        if (moment().subtract(1, 'day').format('YYYY-MM-DD') === window.sessionStorage.getItem('copiedMeds')) {
          self.alreadyCopied = true;

        }
        else {
          self.alreadyCopied = false;
        }
      }
      else {
        self.alreadyCopied = false;
        MedicationApi.checkForDosages(moment().subtract(1, 'day').format('YYYY-MM-DD'))
          .then(function (response) {

            if(response.status === 201) {
              self.hasMedsToCopy = false;
              return;
            }

            self.yesterdayDosageCount = response.data.count;
            self.hasMedsToCopy = true;
          })
          .catch(function (error) {
            self.hasMedsToCopy = false;
          })
      }


      self.$EventBus.$on('medicationPusherReceived', function (model) {
        self.$nextTick(function () {
          self.data = model;
          self.$forceUpdate();
        });
      });

      self.$EventBus.$on('medicationReadingUpdated', function () {
        self.$store.commit('setMedicationCard', {});
        self.loadData();
      });

      self.loadData();
    },
    methods: {
      copyYesterdaysMedication() {
        let self = this;
        self.$alert({
          title: "Are you sure?",
          text: "You are repeating yesterday's medication",
          type: "warning",
          showCancelButton: true,
          showLoaderOnConfirm: false,
          confirmButtonColor: "white",
          cancelButtonColor: "white",
          preConfirm: function () {
            return MedicationApi.copyDosages(moment().subtract(1, 'day').format('YYYY-MM-DD'))
          }
        })
          .then(function (response) {
            self.$alert({
              title: "Success!",
              text: "Dosages from yesterday were copied to today",
              type: "success",
              showCancelButton: false,
              confirmButtonColor: "white"
            });
            self.alreadyCopied = true;
            window.sessionStorage.setItem('copiedMeds', moment().subtract(1, 'day').format('YYYY-MM-DD'));


            self.$EventBus.$emit('medicationReadingAdded');
            self.$EventBus.$emit('updateGraph');
            self.loadData();


          }.bind(self))
          .catch(self.$alert.noop)
      },
      goToSettings() {
        let self = this;
        self.$router.push({
          path: '/settings/medications'
        })
      },
      saveReading() {
        let self = this;
        self.isSaving = true;
        self.$validator.validateAll()
          .then(function (valid) {
            if (valid) {
              if (!_.has(self.model, 'date')) {
                self.model.date = moment().format();
              }
              MedicationApi.postMedicationsTracking(self.model)
                .then(function (response) {
                  self.$store.commit('setMedicationCard', {});
                  self.isSaving = false;
                  self.$gtag.event('ReadingAdded',{
                    'event_category': 'MedicationCard',
                    'event_label': 'Added reading',
                    'value': 1
                  });
                  if (self.$route.name === 'Dashboard') {
                    TweenLite.to(self.scroller, 0.2, {
                      scrollTo: {
                        y: self.$el,
                        offsetY: 16
                      }
                    })
                  }
                  else {
                    self.$EventBus.$emit('medicationReadingAdded');
                    self.$EventBus.$emit('updateGraph');
                  }
                  self.$toastr.success(self.$t('message.cardMedicationAdded'));
                  self.loadData();
                })
            }
            else {
              self.isSaving = false;
            }
          })
          .catch(function () {
            self.isSaving = false;
          })
      },
      closeInput() {
        let self = this;
        self.isOpenInput = false;
        self.$gtag.event('Closed',{
          'event_category': 'MedicationCard',
          'event_label': 'Closed card',
          'value': 0
        });
      },
      openInput() {
        let self = this;
        self.isOpenInput = !self.isOpenInput;

        if (self.isOpenInput) {
          self.$gtag.event('Open',{
            'event_category': 'MedicationCard',
            'event_label': 'Opened card',
            'value': 0
          });
        }
        if (!self.isOpenInput) {
          self.$gtag.event( 'Closed',{
            'event_category': 'MedicationCard',
            'event_label': 'Closed card',
            'value': 0
          });
        }

      },
      loadData() {
        let self = this;
        self.loading = true;
        self.isOpenInput = false;
        MedicationApi.getUserMedications()
          .then(function (response) {
            self.medications = response.data;
            const medicationsLength = self.medications.length;
            if (medicationsLength > 0) {
              if (self.activeMedications.length > 0) {
                self.hasMedications = true;
                self.model.tracking_id = self.activeMedications[0].tracking_id;
              }
            }
            else {
              self.hasMedications = false;
            }
          });
        if (_.has(self.$store.state.cards.medication, 'dosage')) {
          self.loading = false;
          self.data = self.$store.state.cards.medication;
        }
        else {
          MedicationApi.getMedicationsTrackingCard()
            .then(function (response) {
              const status = response.status;
              if (status === 200) {
                self.$store.commit('setMedicationCard', response.data);
                self.data = response.data;
                self.loading = false;
              }
              else {
                if (status === 201) {
                  self.data = response.data;
                  self.loading = false;
                }
              }
            })
        }
      },
      goToDetail() {
        let self = this;
        self.$router.push({
          path: '/tracking/medication/'
        })
      }
    },
    computed: {
      activeMedications() {
        let self = this;
        return self.medications.filter(function (item) {
          return item.active === true;
        });
      },
      activeLabel() {
        let self = this;
        const tracking_id = self.model.tracking_id;
        if (self.medications.length > 0) {
          const unitLabel = self.medications.filter(function (item) {
            return item.tracking_id === tracking_id
          })[0].unit_label;
          return unitLabel;
        }
      }
    },
    beforeDestroy() {
      this.$EventBus.$off('medicationPusherReceived');
      this.$EventBus.$off('medicationReadingUpdated');
    }
  }
</script>
<style lang="less">
  @import "./medication.less";
</style>



// WEBPACK FOOTER //
// Medication.vue?0a17958a